import { useMemo, useState } from 'react';
import {
  eachDay,
  formatDate,
  formatWeekday,
  isWeekendIso,
  weekNumber,
  type DateRange,
} from '../../lib/dates';
import { ABSENCE_LABELS, ABSENCE_LETTER, ABSENCE_STATUS_LABELS } from '../../lib/labels';
import type { Absence, Employee, Holiday } from '../../lib/types';

export interface DaySelection {
  employee: Employee;
  from: string;
  to: string;
  /** Gesetzt, wenn auf einen bestehenden Eintrag geklickt wurde. */
  absence: Absence | null;
}

interface Props {
  employees: Employee[];
  absences: Absence[];
  holidays: Holiday[];
  range: DateRange;
  canEdit: boolean;
  onSelect: (selection: DaySelection) => void;
  onNameClick: (employee: Employee) => void;
}

/**
 * Verfügbarkeit auf einen Blick: Namen links, Tage oben, ein Feld je Person
 * und Tag. Grün heißt verfügbar, Rot heißt nicht verfügbar.
 *
 * Rot ist bewusst kein eigener Datenbestand, sondern die vorhandene
 * Abwesenheit. Ein zweiter Vorrat „verfügbar/nicht verfügbar" würde
 * unweigerlich vom Urlaubskonto abweichen — und dann wüsste niemand mehr,
 * welcher der beiden stimmt.
 */
export function AvailabilityMatrix({
  employees,
  absences,
  holidays,
  range,
  canEdit,
  onSelect,
  onNameClick,
}: Props) {
  const [drag, setDrag] = useState<{ employeeId: number; from: string; to: string } | null>(null);

  const days = useMemo(() => eachDay(range), [range]);
  const holidayByDay = useMemo(
    () => new Map(holidays.map((holiday) => [holiday.date, holiday.name])),
    [holidays],
  );

  /** Abwesenheit je Person und Tag — einmal vorab statt in jeder Zelle. */
  const absenceByKey = useMemo(() => {
    const map = new Map<string, Absence>();
    for (const absence of absences) {
      for (const day of eachDay({ from: absence.startDate, to: absence.endDate })) {
        map.set(`${absence.employeeId}|${day}`, absence);
      }
    }
    return map;
  }, [absences]);

  /** Kalenderwochen als zusammengefasste Kopfzeile über den Tagen. */
  const weeks = useMemo(() => {
    const result: { label: string; span: number }[] = [];
    for (const day of days) {
      const label = `KW${weekNumber(day)}`;
      const last = result[result.length - 1];
      if (last && last.label === label) last.span += 1;
      else result.push({ label, span: 1 });
    }
    return result;
  }, [days]);

  const finish = () => {
    if (!drag) return;
    const employee = employees.find((entry) => entry.id === drag.employeeId);
    setDrag(null);
    if (!employee) return;

    const [from, to] = drag.from <= drag.to ? [drag.from, drag.to] : [drag.to, drag.from];
    onSelect({ employee, from, to, absence: absenceByKey.get(`${employee.id}|${from}`) ?? null });
  };

  return (
    <div className="avail-scroll" onMouseLeave={() => setDrag(null)} onMouseUp={finish}>
      <div
        className="avail-grid"
        style={{ '--day-count': days.length } as never}
        role="grid"
        aria-label="Verfügbarkeit"
      >
        {/* Kalenderwochen */}
        <div className="avail-corner avail-corner-weeks" />
        {weeks.map((week, index) => (
          <div
            key={`${week.label}-${index}`}
            className="avail-week"
            style={{ gridColumn: `span ${week.span}` }}
          >
            {week.label}
          </div>
        ))}

        {/* Tage */}
        <div className="avail-corner" role="columnheader">
          Mitarbeiter
        </div>
        {days.map((day) => {
          const holiday = holidayByDay.get(day);
          return (
            <div
              key={day}
              role="columnheader"
              className={cls('avail-dayhead', isWeekendIso(day) && 'is-weekend', holiday && 'is-holiday')}
              title={holiday ?? undefined}
            >
              <span className="avail-daynum numeric">{Number(day.slice(8, 10))}</span>
              <span className="avail-dayname">{formatWeekday(day)}</span>
            </div>
          );
        })}

        {/* Eine Zeile je Mitarbeiter */}
        {employees.map((employee) => (
          <Row
            key={employee.id}
            employee={employee}
            days={days}
            absenceByKey={absenceByKey}
            holidayByDay={holidayByDay}
            canEdit={canEdit}
            drag={drag}
            setDrag={setDrag}
            onSelect={onSelect}
            onNameClick={onNameClick}
          />
        ))}
      </div>
    </div>
  );
}

function Row({
  employee,
  days,
  absenceByKey,
  holidayByDay,
  canEdit,
  drag,
  setDrag,
  onSelect,
  onNameClick,
}: {
  employee: Employee;
  days: string[];
  absenceByKey: Map<string, Absence>;
  holidayByDay: Map<string, string>;
  canEdit: boolean;
  drag: { employeeId: number; from: string; to: string } | null;
  setDrag: (value: { employeeId: number; from: string; to: string } | null) => void;
  onSelect: (selection: DaySelection) => void;
  onNameClick: (employee: Employee) => void;
}) {
  return (
    <>
      <div className="avail-name" role="rowheader">
        <button type="button" className="avail-name-text" onClick={() => onNameClick(employee)}>
          {employee.displayName}
        </button>
        {employee.role ? <span className="avail-name-role">{employee.role}</span> : null}
      </div>

      {days.map((day) => {
        const absence = absenceByKey.get(`${employee.id}|${day}`);
        const holiday = holidayByDay.get(day);
        const weekend = isWeekendIso(day);
        const inDrag =
          drag?.employeeId === employee.id &&
          day >= (drag.from <= drag.to ? drag.from : drag.to) &&
          day <= (drag.from <= drag.to ? drag.to : drag.from);

        return (
          <div
            key={day}
            role="gridcell"
            tabIndex={canEdit ? 0 : -1}
            aria-label={describe(employee, day, absence, holiday, weekend)}
            title={describe(employee, day, absence, holiday, weekend)}
            className={cls(
              'avail-cell',
              absence ? 'is-blocked' : 'is-free',
              absence?.status === 'beantragt' && 'is-pending',
              weekend && 'is-weekend',
              holiday && 'is-holiday',
              inDrag && 'is-drag',
            )}
            onMouseDown={() => canEdit && setDrag({ employeeId: employee.id, from: day, to: day })}
            onMouseEnter={() => {
              if (drag?.employeeId === employee.id) setDrag({ ...drag, to: day });
            }}
            onKeyDown={(event) => {
              if (!canEdit || (event.key !== 'Enter' && event.key !== ' ')) return;
              event.preventDefault();
              onSelect({ employee, from: day, to: day, absence: absence ?? null });
            }}
          >
            {absence ? <span className="avail-mark">{ABSENCE_LETTER[absence.type]}</span> : null}
          </div>
        );
      })}
    </>
  );
}

function describe(
  employee: Employee,
  day: string,
  absence: Absence | undefined,
  holiday: string | undefined,
  weekend: boolean,
): string {
  const head = `${employee.displayName} · ${formatDate(day)}`;
  if (absence) {
    const status =
      absence.status === 'genehmigt' ? '' : ` (${ABSENCE_STATUS_LABELS[absence.status]})`;
    const reason = absence.note ? ` — ${absence.note}` : '';
    const span =
      absence.startDate === absence.endDate
        ? ''
        : ` · ${formatDate(absence.startDate)} bis ${formatDate(absence.endDate)}`;
    return `${head}: nicht verfügbar — ${ABSENCE_LABELS[absence.type]}${status}${reason}${span}`;
  }
  if (holiday) return `${head}: ${holiday}`;
  if (weekend) return `${head}: Wochenende`;
  return `${head}: verfügbar`;
}

function cls(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}
